import type { ChatMessageProps } from "@albertine/design-system/components/chat";
import type { ChatMessage as ApiChatMessage } from "@albertine/types/openapi/concierge-v1";
import { useAlbertineTranslation } from "albertine-shared-web";
import { compareAsc } from "date-fns/compareAsc";
import { parseISO } from "date-fns/parseISO";
import { LOCAL_MEMBER_ID } from "../-data/chatApi";
import type { ChatOutboxEntry } from "../-data/chatOutbox";
import type { ChatMessageDisplayContext } from "../-data/mockRequest";
import {
    apiChatMessageToChatMessageProps,
    createLocalChatMessage,
} from "../-utils/chatMessageMapper";
import {
    type ConciergeLinkContext,
    enrichChatMessageWithConciergeLinks,
} from "../-utils/conciergeLinks";
import { groupThreadByDay } from "../-utils/groupThreadByDay";
import {
    annotateThreadClusters,
    groupIntoSenderClusters,
} from "../-utils/messageClusters";
import { collectThreadImageUrls } from "../-utils/threadImageUrls";

export type ThreadItem =
    | {
          kind: "sent";
          id: string;
          sentAt: Date;
          message: ChatMessageProps;
      }
    | {
          kind: "pending";
          id: string;
          sentAt: Date;
          entry: ChatOutboxEntry;
          message: ChatMessageProps;
      };

export type ChatThreadDayGroup = {
    dateKey: string;
    date: Date;
    clusters: ThreadItem[][];
};

type UseChatThreadViewArgs = {
    messages: ApiChatMessage[];
    outboxEntries: readonly ChatOutboxEntry[];
    display?: ChatMessageDisplayContext;
    linkContext?: ConciergeLinkContext;
};

export function useChatThreadView({
    messages,
    outboxEntries,
    display,
    linkContext,
}: UseChatThreadViewArgs): {
    dayGroups: ChatThreadDayGroup[];
    isWaitingForAgentReply: boolean;
    imageUrls: string[];
} {
    const t = useAlbertineTranslation();
    const replyTitle = t("chat.reply_title");
    const you = t("chat.you");
    const outboxBySendingId = new Map(
        outboxEntries.map((entry) => [entry.sendingId, entry]),
    );

    const confirmedSendingIds = new Set(
        messages.flatMap((message) =>
            message.sender.type === "member" && message.sendingId
                ? [message.sendingId]
                : [],
        ),
    );
    const sent: ThreadItem[] = messages.toReversed().map((message) => {
        const mapped = apiChatMessageToChatMessageProps(
            message,
            display,
            replyTitle,
            you,
        );
        return {
            kind: "sent",
            id: getThreadItemRenderId(message),
            sentAt: getThreadItemOrderDate(message, outboxBySendingId),
            message: toThreadMessageProps(mapped, linkContext, message.requestId),
        };
    });
    const pending: ThreadItem[] = outboxEntries
        .filter((entry) => !confirmedSendingIds.has(entry.sendingId))
        .map((entry) => ({
            kind: "pending",
            id: entry.sendingId,
            sentAt: parseISO(entry.createdAt),
            entry,
            message: toThreadMessageProps(
                {
                    ...apiChatMessageToChatMessageProps(
                        createLocalChatMessage(
                            entry.sendingId,
                            { type: "member", memberId: LOCAL_MEMBER_ID, name: you },
                            entry.messageHtml,
                            entry.attachments,
                            entry.createdAt,
                        ),
                        display,
                        replyTitle,
                        you,
                    ),
                    confirmedBookings: entry.confirmedBookings,
                    reply: entry.reply,
                    deliveryStatus: entry.status,
                },
                linkContext,
            ),
        }));

    const threadItems = [...sent, ...pending].toSorted((left, right) =>
        compareAsc(left.sentAt, right.sentAt),
    );
    const dayGroups = groupThreadByDay(annotateThreadClusters(threadItems)).map(
        (group) => ({
            ...group,
            clusters: groupIntoSenderClusters(group.items),
        }),
    );

    return {
        dayGroups,
        isWaitingForAgentReply: isThreadWaitingForAgentReply(threadItems),
        imageUrls: collectThreadImageUrls(threadItems),
    };
}

export function isThreadWaitingForAgentReply(items: readonly ThreadItem[]): boolean {
    let latest: ThreadItem | undefined;

    for (const item of items) {
        if (item.kind === "pending" && item.entry.status === "failed") {
            continue;
        }

        if (!latest || item.sentAt.getTime() >= latest.sentAt.getTime()) {
            latest = item;
        }
    }

    return latest?.message.sender === "member";
}

export function getThreadItemRenderId(message: ApiChatMessage): string {
    return message.sender.type === "member" && message.sendingId
        ? message.sendingId
        : message.id;
}

export function getThreadItemOrderDate(
    message: ApiChatMessage,
    outboxBySendingId: ReadonlyMap<string, ChatOutboxEntry>,
): Date {
    const outboxEntry =
        message.sender.type === "member" && message.sendingId
            ? outboxBySendingId.get(message.sendingId)
            : undefined;

    return parseISO(outboxEntry?.createdAt ?? message.sentAt);
}

function toThreadMessageProps(
    message: ChatMessageProps,
    linkContext: ConciergeLinkContext | undefined,
    messageRequestId?: string | null,
): ChatMessageProps {
    if (!linkContext) {
        return message;
    }

    return enrichChatMessageWithConciergeLinks(message, {
        ...linkContext,
        requestId: messageRequestId ?? linkContext.requestId,
    });
}
